import React, { useRef, useEffect, useState } from 'react';
import {
  Box,
  Flex,
  HStack,
  VStack,
  Text,
  Avatar,
  IconButton,
  Input,
  useColorModeValue,
  InputGroup,
  InputRightElement,
  Tooltip,
  useTheme,
  Divider,
  Popover,
  PopoverTrigger,
  PopoverContent,
  PopoverBody,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  Button,
  Spinner,
  Circle,
} from '@chakra-ui/react';
import { Send, Phone, Video, Info, ChevronLeft, Paperclip, Smile, MoreVertical, Check, CheckCheck, Image, FileText, Music, Film, Camera, User, X, Play, Download } from 'lucide-react';
import { useGlobal } from "../../../../contexts/GlobalContext";
import ItemMessage from './ItemMessage';
import { getWpSetting } from "../../../../redux/actions/settings";
import { useDispatch } from 'react-redux';

// Attachment type definitions for a professional "aesthetic" UI
const ATTACHMENT_TYPES = [
  { key: 'document', label: 'Document', icon: FileText, accept: '.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv', color: 'blue.600', bg: 'blue.50', _darkBg: 'rgba(49, 130, 206, 0.15)' },
  { key: 'image', label: 'Image', icon: Image, accept: 'image/*', color: 'green.600', bg: 'green.50', _darkBg: 'rgba(56, 161, 105, 0.15)' },
  { key: 'video', label: 'Video', icon: Film, accept: 'video/*', color: 'purple.600', bg: 'purple.50', _darkBg: 'rgba(128, 90, 213, 0.15)' },
  { key: 'audio', label: 'Audio', icon: Music, accept: 'audio/*', color: 'orange.500', bg: 'orange.50', _darkBg: 'rgba(221, 107, 32, 0.15)' },
  // { key: 'camera', label: 'Camera', icon: Camera, accept: 'image/*', color: 'pink.500', bg: 'pink.50', _darkBg: 'rgba(213, 63, 140, 0.15)', capture: 'environment' },
  // { key: 'contact', label: 'Contact', icon: User, accept: '.vcf', color: 'teal.600', bg: 'teal.50', _darkBg: 'rgba(49, 151, 149, 0.15)' },
];

// Sub-component: Professional Attachment option button in the popover
const AttachmentOption = ({ type, onClick }) => {
  const IconComp = type.icon;
  const borderColor = useColorModeValue('gray.100', 'gray.700');

  return (
    <VStack
      spacing={2}
      cursor="pointer"
      onClick={onClick}
      role="group"
      align="center"
      _hover={{ transform: 'translateY(-2px)' }}
      transition="all 0.2s ease"
    >
      <Box
        w="50px"
        h="50px"
        borderRadius="xl"
        bg={useColorModeValue(type.bg, type._darkBg)}
        display="flex"
        alignItems="center"
        justifyContent="center"
        border="1px solid"
        borderColor="transparent"
        color={useColorModeValue(type.color, type.color.replace('600', '300').replace('500', '300'))}
        _groupHover={{
          borderColor: useColorModeValue('gray.200', 'gray.600'),
          bg: useColorModeValue(type.bg.replace('50', '100'), type._darkBg)
        }}
        transition="all 0.2s ease"
      >
        <IconComp size={22} strokeWidth={1.5} />
      </Box>
      <Text fontSize="11px" fontWeight="500" color="gray.600" _dark={{ color: 'gray.400' }} letterSpacing="wide">
        {type.label}
      </Text>
    </VStack>
  );
};

const ChatWindow = ({ chat, messages, isPartnerTyping, onSendMessage, onTyping, onBack, onToggleInfo, onLoadMore, hasMore, isLoadingMore }) => {
  const scrollRef = useRef();

  const handleForceDownload = async (url, filename, e) => {
    e.preventDefault();
    e.stopPropagation();
    try {
      const response = await fetch(url);
      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);
    } catch (error) {
      console.error("Download failed:", error);
      window.open(url, '_blank');
    }
  };

  const prevScrollHeightRef = useRef(0);
  const prevMessagesLengthRef = useRef(0);
  const theme = useTheme();
  const dispatch = useDispatch();
  const typingTimeoutRef = useRef(null);
  const isTypingRef = useRef(false);
  const { company } = useGlobal();
  const fileInputRefs = useRef({});
  const { isOpen: isAttachOpen, onOpen: onAttachOpen, onClose: onAttachClose } = useDisclosure();

  const [attachmentData, setAttachmentData] = useState(null);
  const [caption, setCaption] = useState('');
  const [isUploading, setIsUploading] = useState(false);
  const [previewMedia, setPreviewMedia] = useState(null);

  // Handle file selection from hidden inputs
  const handleFileSelect = (type, e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    let actualType = 'DOC';
    if (file.type.startsWith('image/')) actualType = 'IMG';
    else if (file.type.startsWith('video/')) actualType = 'VIDEO';
    else if (file.type.startsWith('audio/')) actualType = 'AUDIO';

    setAttachmentData({
      file,
      type: actualType,
      preview: URL.createObjectURL(file),
      name: file.name
    });
    setCaption('');
    onAttachClose();

    e.target.value = '';
  };

  const handleSendAttachment = async () => {
    if (!attachmentData) return;
    setIsUploading(true);

    try {
      const formData = new FormData();
      formData.append("file", attachmentData.file);
      formData.append("isChat", "true");

      const res = await dispatch(getWpSetting(formData));
      if (res?.data && res.data.length > 0) {
        onSendMessage({ url: res.data[0].url, type: attachmentData.type, text: caption.trim() || undefined });
      }
    } catch (error) {
      console.error("Upload failed", error);
    } finally {
      setIsUploading(false);
      if (attachmentData.preview) {
        URL.revokeObjectURL(attachmentData.preview);
      }
      setAttachmentData(null);
      setCaption('');
    }
  };

  // Theme-aware colors for a professional look
  const bgColor = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.100', 'gray.700');
  const inputBg = useColorModeValue('gray.50', 'gray.700');
  const emptyChatBg = useColorModeValue('gray.50', 'gray.900');
  const messagesBg = useColorModeValue('#f8f9fa', 'gray.900');
  const bubbleBgOther = useColorModeValue('white', 'gray.700');
  const textColor = useColorModeValue('gray.800', 'white');
  const mutedText = useColorModeValue('gray.500', 'gray.400');
  const activeIconColor = useColorModeValue('blue.500', 'blue.300');
  const headerBg = useColorModeValue('white', 'gray.800');
  const callBtnBg = useColorModeValue('blue.50', 'whiteAlpha.100');
  const emptyChatIconBg = useColorModeValue('white', 'gray.800');

  // Auto scroll to bottom when messages change
  useEffect(() => {
    if (scrollRef.current) {
      const el = scrollRef.current;
      const isScrollNearBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 150;

      if (messages.length > prevMessagesLengthRef.current && prevMessagesLengthRef.current !== 0 && el.scrollTop === 0) {
        // Appended to top (pagination)
        el.scrollTop = el.scrollHeight - prevScrollHeightRef.current;
      } else if (isScrollNearBottom || messages.length <= 10) {
        // Scroll to bottom normally for new received message
        el.scrollTop = el.scrollHeight;
      }

      prevScrollHeightRef.current = el.scrollHeight;
      prevMessagesLengthRef.current = messages.length;
    }
  }, [messages, isPartnerTyping]);

  const handleScroll = (e) => {
    if (e.target.scrollTop === 0 && hasMore && !isLoadingMore) {
      if (onLoadMore) onLoadMore();
    }
  };

  const handleInputChange = (e) => {
    if (onTyping) {
      // Only send "typing started" once per typing session
      if (!isTypingRef.current) {
        isTypingRef.current = true;
        onTyping(true);
      }

      // Reset the stop timer on every keystroke
      if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);

      typingTimeoutRef.current = setTimeout(() => {
        isTypingRef.current = false;
        onTyping(false);
      }, 2000);
    }
  };

  const handleSendMessageInternal = (text) => {
    if (!text.trim()) return;
    onSendMessage({ text, type: "TEXT" });
    if (onTyping) {
      onTyping(false);
      if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
    }
  };

  if (!chat) {
    return (
      <Flex flex={1} w={{ base: '100%', md: 'calc(100% - 360px)' }} maxW="100%" align="center" justify="center" direction="column" bg={emptyChatBg}>
        <Box textAlign="center" p={12}>
          <Box mb={6} display="inline-block" p={6} borderRadius="full" bg={emptyChatIconBg} shadow="sm">
            <Send size={48} color={theme.colors.blue[400]} />
          </Box>
          <Text fontSize="2xl" fontWeight="700" color={textColor} mb={2}>
            Select a conversation
          </Text>
          <Text color={mutedText} fontSize="md" maxW="300px" mx="auto">
            Choose a chat from the sidebar to start messaging your team members.
          </Text>
        </Box>
      </Flex>
    );
  }

  // Helper to format the divider date
  const formatDividerDate = (date) => {
    const d = new Date(date);
    const today = new Date();
    const yesterday = new Date();
    yesterday.setDate(today.getDate() - 1);

    if (d.toDateString() === today.toDateString()) return 'Today';
    if (d.toDateString() === yesterday.toDateString()) return 'Yesterday';

    return d.toLocaleDateString([], { month: 'long', day: 'numeric', year: 'numeric' });
  };

  return (
    <Flex flex={1} w={{ base: '100%', md: 'calc(100% - 360px)' }} maxW={{ base: '100%', md: 'calc(100% - 360px)' }} h="full" direction="column" bg={bgColor} shadow="xl" position="relative">
      {/* Header - Receiving User Name and Call Actions */}
      <Flex
        px={6}
        py={4}
        borderBottom="1px"
        borderColor={borderColor}
        justify="space-between"
        align="center"
        bg={headerBg}
        zIndex="10"
      >
        <HStack spacing={4}>
          <IconButton
            display={{ base: 'flex', md: 'none' }}
            variant="ghost"
            icon={<ChevronLeft size={24} />}
            onClick={onBack}
            aria-label="Back to messages"
            borderRadius="full"
          />
          <Avatar size="md" name={chat.name} src={chat.avatar} bg="blue.500" color="white">
            {/* <AvatarBadge boxSize="1em" bg={chat.isOnline ? "green.400" : "gray.400"} borderColor={bgColor} border="2px solid" /> */}
          </Avatar>
          <Box>
            <Text fontWeight="700" fontSize="md" color={textColor}>{chat.name}</Text>
            {chat.phone && (
              <HStack spacing={1}>
                <Text fontSize="xs" color={mutedText} fontWeight="600">
                  {chat.phone}
                </Text>
              </HStack>
            )}
          </Box>
        </HStack>

        <HStack spacing={2}>
          {/* <Tooltip label="Voice Call" placement="bottom">
            <IconButton
              variant="light"
              bg={callBtnBg}
              color={activeIconColor}
              _hover={{ bg: 'blue.500', color: 'white' }}
              size="md"
              borderRadius="full"
              icon={<Phone size={20} />}
              aria-label="Call"
            />
          </Tooltip> */}
          {/* <Tooltip label="Video Call" placement="bottom">
            <IconButton
              variant="light"
              bg={callBtnBg}
              color={activeIconColor}
              _hover={{ bg: 'blue.500', color: 'white' }}
              size="md"
              borderRadius="full"
              icon={<Video size={20} />}
              aria-label="Video Call"
            />
          </Tooltip> */}
          {/* <Divider orientation="vertical" h="24px" mx={2} /> */}
          {/* <IconButton
            variant="ghost"
            size="md"
            borderRadius="full"
            icon={<Info size={20} />}
            onClick={onToggleInfo}
            aria-label="Conversation info"
          />
          <IconButton
            variant="ghost"
            size="md"
            borderRadius="full"
            icon={<MoreVertical size={20} />}
            aria-label="More options"
          /> */}
        </HStack>
      </Flex>

      {/* Messages Area */}
      <Box
        flex="1"
        overflowY="auto"
        p={6}
        ref={scrollRef}
        bg={messagesBg}
        onScroll={handleScroll}
        css={{
          '&::-webkit-scrollbar': { width: '6px' },
          '&::-webkit-scrollbar-track': { background: 'transparent' },
          '&::-webkit-scrollbar-thumb': { background: theme.colors.gray[300], borderRadius: '10px' },
        }}
      >
        <VStack spacing={6} align="stretch">
          {isLoadingMore && (
            <Flex justify="center" py={4}>
              <Spinner size="sm" color="blue.500" />
            </Flex>
          )}
          {messages.map((msg, index) => {
            const isMe = msg.syncCode === company?.setting?.syncCode;
            const isItem = msg.type === 'ITEM';
            const timeStr = msg.time || (msg.createdAt ? new Date(msg.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '');

            // Logic for date divider
            const currentDate = msg.createdAt ? new Date(msg.createdAt).toDateString() : null;
            const previousDate = index > 0 && messages[index - 1].createdAt ? new Date(messages[index - 1].createdAt).toDateString() : null;
            const showDivider = currentDate && currentDate !== previousDate;

            return (
              <React.Fragment key={index}>
                {showDivider && (
                  <Flex align="center" py={4}>
                    <Divider borderColor={borderColor} />
                    <Text
                      px={4}
                      fontSize="xs"
                      fontWeight="700"
                      color={mutedText}
                      whiteSpace="nowrap"
                      bg={messagesBg}
                      borderRadius="full"
                    >
                      {formatDividerDate(msg.createdAt)}
                    </Text>
                    <Divider borderColor={borderColor} />
                  </Flex>
                )}

                <Flex justify={isMe ? 'flex-end' : 'flex-start'} position="relative">
                  {isItem ? (
                    <VStack align={isMe ? 'flex-end' : 'flex-start'} spacing={1} maxW="75%">
                      <ItemMessage data={msg.text} isMe={isMe} time={timeStr} status={msg.isRead} />
                    </VStack>
                  ) : (
                    <Box
                      maxW="75%"
                      bg={isMe ? '#38358C' : bubbleBgOther}
                      color={isMe ? 'white' : textColor}
                      p={(msg.type === 'IMG' || msg.type === 'VIDEO' || msg.type === 'DOC') ? 1 : 3}
                      px={(msg.type === 'IMG' || msg.type === 'VIDEO' || msg.type === 'DOC') ? 1 : 5}
                      borderRadius="2xl"
                      borderBottomRightRadius={isMe ? '4px' : '2xl'}
                      borderBottomLeftRadius={isMe ? '2xl' : '4px'}
                      boxShadow="sm"
                      transition="all 0.2s"
                      _hover={{ boxShadow: 'md' }}
                      display="flex"
                      flexDirection="column"
                    >
                      {msg.type === 'IMG' && msg.url ? (
                        <Flex direction="column" position="relative">
                          <Box position="relative">
                            <Box
                              as="img"
                              crossOrigin="anonymous"
                              src={`https://api.zeronine.ai${msg.url}`}
                              maxH="200px"
                              objectFit="contain"
                              borderRadius="xl"
                              borderBottomRadius={msg.text ? "sm" : "xl"}
                              mb={msg.text ? 1 : 0}
                              w="full"
                              bg="white"
                              cursor="pointer"
                              onClick={() => setPreviewMedia({ url: `https://api.zeronine.ai${msg.url}`, type: 'IMG' })}
                            />
                            <Circle
                              as="button"
                              onClick={(e) => handleForceDownload(`https://api.zeronine.ai${msg.url}`, msg.url.split('/').pop(), e)}
                              position="absolute"
                              top={2}
                              right={2}
                              size="32px"
                              bg="blackAlpha.500"
                              _hover={{ bg: 'blackAlpha.700' }}
                              transition="all 0.2s"
                              zIndex={2}
                            >
                              <Download size={18} color="white" />
                            </Circle>
                          </Box>
                          {msg.text && <Text fontSize="md" lineHeight="tall" px={2} pt={1} pb={1}>{msg.text}</Text>}
                        </Flex>
                      ) : msg.type === 'VIDEO' && msg.url ? (
                        <Flex direction="column" position="relative">
                          <Box position="relative">
                            <Box as="video" crossOrigin="anonymous" cursor="pointer" preload="metadata" playsInline src={`https://api.zeronine.ai${msg.url}`} maxH="200px" borderRadius="xl" borderBottomRadius={msg.text ? "sm" : "xl"} mb={msg.text ? 1 : 0} w="full" bg="blackAlpha.800" objectFit="cover" onClick={() => setPreviewMedia({ url: `https://api.zeronine.ai${msg.url}`, type: 'VIDEO' })} />
                            <Flex position="absolute" top="0" left="0" right="0" bottom={msg.text ? "4px" : "0"} justify="center" align="center" pointerEvents="none">
                              <Circle size="50px" bg="blackAlpha.600" backdropFilter="blur(2px)">
                                <Play size={24} color="white" fill="white" style={{ marginLeft: '4px' }} />
                              </Circle>
                            </Flex>
                            <Circle
                              as="button"
                              onClick={(e) => handleForceDownload(`https://api.zeronine.ai${msg.url}`, msg.url.split('/').pop(), e)}
                              position="absolute"
                              top={2}
                              right={2}
                              size="32px"
                              bg="blackAlpha.500"
                              _hover={{ bg: 'blackAlpha.700' }}
                              transition="all 0.2s"
                              zIndex={2}
                            >
                              <Download size={16} color="white" />
                            </Circle>
                          </Box>
                          {msg.text && <Text fontSize="md" lineHeight="tall" px={2} pt={1} pb={1}>{msg.text}</Text>}
                        </Flex>
                      ) : msg.type === 'AUDIO' && msg.url ? (
                        <Box>
                          <Box as="audio" cursor="pointer" controls src={`https://api.zeronine.ai${msg.url}`} w="250px" mb={msg.text ? 2 : 0} />
                          {msg.text && <Text fontSize="md" lineHeight="tall">{msg.text}</Text>}
                        </Box>
                      ) : msg.type === 'DOC' && msg.url ? (
                        <Flex direction="column" position="relative">
                          <Flex
                            as="a"
                            href={`https://api.zeronine.ai${msg.url}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            align="center"
                            bg="blackAlpha.200"
                            _dark={{ bg: 'blackAlpha.400' }}
                            p={2}
                            borderRadius="xl"
                            borderBottomRadius={msg.text ? "sm" : "xl"}
                            overflow="hidden"
                            mb={msg.text ? 1 : 0}
                            w="full"
                            _hover={{ bg: "blackAlpha.300", _dark: { bg: 'blackAlpha.500' } }}
                            transition="all 0.2s"
                          >
                            <Flex
                              align="center"
                              justify="center"
                              bg={msg.url.toLowerCase().endsWith('.pdf') ? "#e5252a" : "gray.600"}
                              borderRadius="md"
                              w="50px"
                              h="50px"
                              minW="50px"
                            >
                              {msg.url.toLowerCase().endsWith('.pdf') ? (
                                <Text color="white" fontSize="xs" fontWeight="bold">PDF</Text>
                              ) : (
                                <FileText size={24} color="white" />
                              )}
                            </Flex>
                            <VStack ml={3} align="start" spacing={0} overflow="hidden" flex={1} justify="center">
                              <Text fontWeight="medium" fontSize="15px" color={isMe ? 'white' : 'inherit'} noOfLines={1} w="full">
                                {msg.url.split('/').pop()}
                              </Text>
                              <Text fontSize="xs" color={isMe ? 'whiteAlpha.700' : 'gray.500'} mt={0.5}>
                                {msg.url.toLowerCase().endsWith('.pdf') ? 'PDF Document' : 'Document'}
                              </Text>
                            </VStack>
                            <Flex
                              as="button"
                              onClick={(e) => handleForceDownload(`https://api.zeronine.ai${msg.url}`, msg.url.split('/').pop(), e)}
                              ml={2} mr={2} opacity={0.7} align="center" justify="center"
                              _hover={{ opacity: 1, transform: 'scale(1.1)' }}
                              transition="all 0.2s"
                              cursor="pointer"
                            >
                              <Download size={20} color={isMe ? 'white' : 'gray'} />
                            </Flex>
                          </Flex>
                          {msg.text && <Text fontSize="md" lineHeight="tall" px={2} pt={1} pb={1}>{msg.text}</Text>}
                        </Flex>
                      ) : (
                        <Text fontSize="md" lineHeight="tall">{msg.text}</Text>
                      )}

                      <HStack
                        alignSelf="flex-end"
                        spacing={1}
                        mt={msg.type === 'TEXT' ? 1 : 0}
                        mb={(msg.type === 'IMG' || msg.type === 'VIDEO' || msg.type === 'DOC') ? 1 : 0}
                        px={(msg.type === 'IMG' || msg.type === 'VIDEO' || msg.type === 'DOC') ? 2 : 0}
                        opacity={0.8}
                      >
                        <Text
                          fontSize="10px"
                          fontWeight="600"
                          letterSpacing="wider"
                        >
                          {timeStr}
                        </Text>
                        {isMe && (
                          msg.isRead ? (
                            <CheckCheck size={14} color="#93defeff" strokeWidth={2} />
                          ) : (
                            <Check size={14} color="white" strokeWidth={2.5} />
                          )
                        )}
                      </HStack>
                    </Box>
                  )}
                </Flex>
              </React.Fragment>
            );
          })}

          {/* Typing Indicator */}
          {isPartnerTyping && (
            <Flex justify="flex-start">
              <Box
                bg={bubbleBgOther}
                color={mutedText}
                px={4}
                py={2}
                borderRadius="2xl"
                borderBottomLeftRadius="4px"
                boxShadow="sm"
              >
                <HStack spacing={1}>
                  <Text fontSize="xs" fontWeight="600">{chat.name} is typing</Text>
                  <Box className="typing-dots" display="flex">
                    <Text as="span" animation="pulse 1.5s infinite" sx={{ '@keyframes pulse': { '0%, 100%': { opacity: 0.2 }, '50%': { opacity: 1 } } }}>.</Text>
                    <Text as="span" animation="pulse 1.5s infinite 0.2s" sx={{ '@keyframes pulse': { '0%, 100%': { opacity: 0.2 }, '50%': { opacity: 1 } } }}>.</Text>
                    <Text as="span" animation="pulse 1.5s infinite 0.4s" sx={{ '@keyframes pulse': { '0%, 100%': { opacity: 0.2 }, '50%': { opacity: 1 } } }}>.</Text>
                  </Box>
                </HStack>
              </Box>
            </Flex>
          )}
        </VStack>
      </Box>

      {/* Input Area */}
      {/* Hidden file inputs for each attachment type */}
      {ATTACHMENT_TYPES.map((type) => (
        <input
          key={type.key}
          ref={(el) => { fileInputRefs.current[type.key] = el; }}
          type="file"
          accept={type.accept}
          capture={type.capture || undefined}
          style={{ display: 'none' }}
          onChange={(e) => handleFileSelect(type.key, e)}
        />
      ))}

      {/* Input Area */}
      <Box p={6} borderTop="1px" borderColor={borderColor} bg={bgColor}>
        <InputGroup size="lg">
          <Input
            pr="8rem"
            placeholder="Write a message..."
            bg={inputBg}
            border="1px solid"
            borderColor={borderColor}
            _focus={{ borderColor: 'blue.400', boxShadow: '0 0 0 1px #4299E1' }}
            borderRadius="xl"
            fontSize="md"
            h="56px"
            onChange={handleInputChange}
            onKeyPress={(e) => {
              if (e.key === 'Enter') {
                handleSendMessageInternal(e.target.value);
                e.target.value = '';
              }
            }}
          />
          <InputRightElement width="8.5rem" h="56px">
            <HStack mr={2} spacing={1}>
              {/* <IconButton variant="ghost" size="md" borderRadius="full" icon={<Smile size={20} />} /> */}

              {/* Attachment Popover */}
              <Popover
                isOpen={isAttachOpen}
                onopen={onAttachOpen}
                onClose={onAttachClose}
                placement="top-end"
                closeOnBlur={true}
              >
                <PopoverTrigger>
                  <IconButton
                    variant="ghost"
                    size="md"
                    borderRadius="full"
                    icon={<Paperclip size={20} />}
                    onClick={isAttachOpen ? onAttachClose : onAttachOpen}
                    aria-label="Attach file"
                    color={isAttachOpen ? 'blue.500' : undefined}
                    bg={isAttachOpen ? 'blue.50' : undefined}
                    _dark={{ bg: isAttachOpen ? 'whiteAlpha.100' : undefined }}
                  />
                </PopoverTrigger>
                <PopoverContent
                  w="220px"
                  border="none"
                  boxShadow="0 20px 60px rgba(0,0,0,0.15)"
                  borderRadius="2xl"
                  overflow="hidden"
                  bg="white"
                  _dark={{ bg: 'gray.800' }}
                  p={4}
                >
                  <PopoverBody p={0}>
                    <Box
                      display="grid"
                      gridTemplateColumns="repeat(3, 1fr)"
                      gap={4}
                      pt={1}
                    >
                      {ATTACHMENT_TYPES.map((type) => (
                        <AttachmentOption
                          key={type.key}
                          type={type}
                          onClick={() => fileInputRefs.current[type.key]?.click()}
                        />
                      ))}
                    </Box>
                  </PopoverBody>
                </PopoverContent>
              </Popover>

              <IconButton
                colorScheme="blue"
                size="md"
                borderRadius="full"
                icon={<Send size={18} />}
                shadow="md"
                _hover={{ shadow: 'lg', transform: 'translateY(-1px)' }}
                _active={{ transform: 'translateY(0)' }}
                onClick={(e) => {
                  const input = e.currentTarget.closest('.chakra-input__group').querySelector('input');
                  handleSendMessageInternal(input.value);
                  input.value = '';
                }}
              />
            </HStack>
          </InputRightElement>
        </InputGroup>
      </Box>

      {/* Attachment Preview Overlay */}
      {attachmentData && (
        <Flex
          position="absolute"
          top={0}
          left={0}
          right={0}
          bottom={0}
          bg="#e9edef"
          _dark={{ bg: '#0b141a' }}
          zIndex={20}
          direction="column"
        >
          {/* Top Bar */}
          <Flex justify="flex-start" align="center" p={4}>
            <IconButton
              icon={<X size={26} />}
              variant="ghost"
              onClick={() => {
                if (!isUploading) {
                  if (attachmentData?.preview) URL.revokeObjectURL(attachmentData.preview);
                  setAttachmentData(null);
                }
              }}
              color="gray.600"
              _dark={{ color: "gray.300" }}
              _hover={{ bg: "blackAlpha.100", _dark: { bg: "whiteAlpha.200" } }}
              isDisabled={isUploading}
              aria-label="Close preview"
            />
          </Flex>

          {/* Media Container */}
          <Flex flex={1} justify="center" align="center" p={4} overflow="hidden">
            {attachmentData?.type === 'IMG' && (
              <Box as="img" src={attachmentData.preview} maxH="100%" maxWidth="100%" objectFit="contain" />
            )}
            {attachmentData?.type === 'VIDEO' && (
              <Box as="video" src={attachmentData.preview} controls maxH="100%" maxWidth="100%" />
            )}
            {attachmentData?.type === 'AUDIO' && (
              <Box as="audio" src={attachmentData.preview} controls w="300px" />
            )}
            {attachmentData?.type === 'DOC' && (
              <VStack spacing={4}>
                <FileText size={64} color="gray" />
                <Text fontWeight="medium" textAlign="center" wordBreak="break-word" maxW="400px" color="gray.800" _dark={{ color: "gray.100" }}>{attachmentData.name}</Text>
              </VStack>
            )}
          </Flex>

          {/* Bottom Bar */}
          <Box p={4} pb={8} bg="#f0f2f5" _dark={{ bg: '#202c33' }}>
            <Flex maxW="800px" mx="auto" w="full" align="center" gap={4}>
              <InputGroup size="lg" flex={1}>
                <Input
                  placeholder="Type a message"
                  value={caption}
                  onChange={(e) => setCaption(e.target.value)}
                  isDisabled={isUploading}
                  bg="white"
                  _dark={{ bg: '#2a3942', color: 'gray.100' }}
                  border="none"
                  borderRadius="md"
                  px={4}
                  _focus={{ boxShadow: 'none' }}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter' && !isUploading) {
                      handleSendAttachment();
                    }
                  }}
                />
              </InputGroup>

              <IconButton
                colorScheme="whatsapp"
                bg="#00a884"
                color="white"
                borderRadius="full"
                size="lg"
                w="50px"
                h="50px"
                minW="50px"
                icon={isUploading ? <Spinner size="sm" /> : <Send size={20} />}
                onClick={handleSendAttachment}
                isDisabled={isUploading}
                _hover={{ bg: '#029072' }}
                aria-label="Send attachment"
              />
            </Flex>
          </Box>
        </Flex>
      )}
      {/* Media Preview Lightbox */}
      <Modal isOpen={!!previewMedia} onClose={() => setPreviewMedia(null)} size="6xl" isCentered>
        <ModalOverlay bg="blackAlpha.800" backdropFilter="blur(5px)" />
        <ModalContent bg="transparent" boxShadow="none" onClick={() => setPreviewMedia(null)}>
          <ModalCloseButton color="white" size="lg" zIndex={20} />
          <ModalBody display="flex" justifyContent="center" alignItems="center" p={4} onClick={() => setPreviewMedia(null)}>
            {previewMedia?.type === 'IMG' ? (
              <Box
                as="img"
                crossOrigin="anonymous"
                src={previewMedia.url}
                maxH="90vh"
                maxW="100%"
                objectFit="contain"
                cursor="zoom-out"
                onClick={(e) => {
                  e.stopPropagation();
                  setPreviewMedia(null);
                }}
              />
            ) : (
              <Box
                as="video"
                crossOrigin="anonymous"
                controls
                preload="metadata"
                playsInline
                src={previewMedia?.url}
                maxH="90vh"
                maxW="100%"
                onClick={(e) => e.stopPropagation()}
              />
            )}
          </ModalBody>
        </ModalContent>
      </Modal>

    </Flex>
  );
};

export default ChatWindow;
